import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LoadingProgress } from '../../layout';
import { fromAccounts, fromBillingInformation, fromPaymentMethod, useAppDispatch } from '../../store';
import { Page } from '../../components/page';
import { notFound, useQuery } from '../../routes';
import { PaymentMethodSection } from './PaymentMethodSection';
import { BillingInfoSection } from './BillingInfoSection';
import { BillingLoadError } from './BillingLoadError';

const useStyles = makeStyles(theme => ({
  pageTitle: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: '-.01em',
    color: '#16141d',
  },
  pageSubtitle: {
    fontSize: 13.5,
    color: '#5b6661',
    marginTop: theme.spacing(0.625),
  },
}));

const PaymentMethod = () => {
  const classes = useStyles();
  const { get } = useQuery();
  const dispatch = useAppDispatch();
  const accountId = Number(get('accountId'));
  const billingStatus = useSelector(fromBillingInformation.selectStatusByAccountId(accountId));
  const billingRow = useSelector(fromBillingInformation.selectOneByAccountId(accountId));
  // An ordinary failure with a cached row keeps showing that row (ADR-13); without one, the empty
  // form would read as "no billing information yet", so show the error and a way to retry.
  const billingLoadFailed = billingStatus === 'error' && !billingRow;

  const fetchInfo = async () => {
    if (accountId) {
      const billingRequest = dispatch(fromBillingInformation.doGetBillingInformation(accountId));
      // The card list is requested here, in parallel with the other requests, rather than from
      // PaymentMethodSection's mount effect (Page mounts its children twice, so that sent it
      // twice). It is deliberately not awaited: a slow list must not withhold billing.
      dispatch(fromPaymentMethod.doGetPaymentMethod(accountId));
      await Promise.all([
        dispatch(fromAccounts.doCheckPublicDomain(accountId)),
        dispatch(fromPaymentMethod.doGetDefaultPaymentMethod()),
        billingRequest,
      ]);
    }
  };

  if (!accountId || Number.isNaN(accountId)) return <Navigate to={notFound.path} replace />;

  return (
    <Page
      id="payment-checkout"
      headerId="page.payment.payment.title"
      fetchData={fetchInfo}
      hideHeader
    >
      <Box display="flex" justifyContent="center">
        <Box width="100%" maxWidth={680} pt={5} pb={9}>
          <Typography className={classes.pageTitle}>
            <FormattedMessage id="page.payment.v2.title" />
          </Typography>
          <Typography className={classes.pageSubtitle}>
            <FormattedMessage id="page.payment.v2.subtitle" />
          </Typography>
          {billingStatus === 'idle' || billingStatus === 'pending'
            ? <LoadingProgress />
            : (
              <>
                <PaymentMethodSection accountId={accountId} />
                {billingLoadFailed
                  ? (
                    <BillingLoadError onRetry={() => {
                      dispatch(fromBillingInformation.doGetBillingInformation(accountId));
                    }}
                    />
                  )
                  : <BillingInfoSection key={accountId} accountId={accountId} />}
              </>
            )}
        </Box>
      </Box>
    </Page>
  );
};

export default PaymentMethod;
